/**
 * Joins a snapshot's `core`, `shared` and `private` blocks back together for rendering.
 * Projections hold participant ids; names come from the roster, and an entry whose
 * participant is not in the roster is dropped rather than rendered nameless.
 */

import type { CoreBlock, ParticipantId, RequestView, Snapshot, ViewerView } from '@gsf/contract';
import { namesFrom, rosterFrom, type Standing } from '@gsf/kit';
import type {
  JeopardyHostPrivate,
  JeopardyPlayerPrivate,
  JeopardyShared,
  JeopardySharedAnswer,
  JeopardySharedBuzz,
} from '@gsf/games';

/** A buzz with its name attached, in queue order. `order` is its index + 1. */
export interface BuzzRow extends JeopardySharedBuzz {
  readonly name: string;
}

/**
 * A submitted answer as this viewer sees it: the host and the owning player read their
 * private blocks; the room gets `text` from grading onwards and `comment` never.
 */
export interface AnswerRow extends JeopardySharedAnswer {
  readonly name: string;
  readonly comment: string | null;
}

/**
 * The kinds Jeopardy asks for. Narrow on purpose: `ask()` looks a request up by kind,
 * which is only unambiguous because Jeopardy never has two of one kind open at once.
 */
export type AskKind =
  | 'press'
  | 'text'
  | 'number'
  | 'choice'
  | 'wager'
  | 'pickParticipant'
  | 'verdict'
  | 'gradeList';

export interface JeopardyView {
  readonly seq: number;
  readonly core: CoreBlock;
  readonly you: ViewerView;
  readonly shared: JeopardyShared;
  /** Present only on the host's screen. */
  readonly host: JeopardyHostPrivate | null;
  /** Present only on a player's phone. */
  readonly me: JeopardyPlayerPrivate | null;

  readonly standings: readonly Standing[];
  readonly buzzes: readonly BuzzRow[];
  readonly answers: readonly AnswerRow[];
  /** The clue's prompt and answer, for whichever screen this is. */
  readonly prompt: string | null;
  readonly clueAnswer: string | null;
  /** The daily-double bet, or null when the room may not see it yet. */
  readonly wagerAmount: number | null;

  /** Every open request aimed at this screen, straight off the snapshot. */
  readonly requests: readonly RequestView[];

  nameOf(id: ParticipantId | string | null): string | null;
  scoreOf(id: ParticipantId | string | null): number;
  /**
   * The open request of this kind, or null. Controls render because a request is on the
   * snapshot, never from phase checks, so a control that appears is one core will accept.
   */
  ask(kind: AskKind): RequestView | null;
}

/** Which surface this snapshot was built for. */
export function audienceKind(snapshot: Snapshot): ViewerView['audience']['kind'] {
  return snapshot.you.audience.kind;
}

export function jeopardyView(snapshot: Snapshot): JeopardyView {
  const shared = snapshot.shared as unknown as JeopardyShared;
  const kind = snapshot.you.audience.kind;
  const host = kind === 'host' ? (snapshot.private as unknown as JeopardyHostPrivate) : null;
  const me = kind === 'player' ? (snapshot.private as unknown as JeopardyPlayerPrivate) : null;
  const { core, you } = snapshot;

  const { nameOf, scoreOf } = namesFrom(core);
  const standings = rosterFrom(core);

  const buzzes: BuzzRow[] = shared.buzzes.flatMap((buzz) => {
    const name = nameOf(buzz.participantId);
    return name === null ? [] : [{ ...buzz, name }];
  });

  const answers: AnswerRow[] = shared.answers.flatMap((answer) => {
    const name = nameOf(answer.participantId);
    if (name === null) return [];
    const mine = me !== null && answer.participantId === you.participantId;
    return [
      {
        ...answer,
        name,
        text: host
          ? (host.answerTexts[answer.participantId] ?? null)
          : mine
            ? me.submittedAnswer
            : answer.text,
        // A host's reply is between them and one player. It never reaches the TV, even
        // at grading time when the answer text itself becomes public.
        comment: host ? (host.comments[answer.participantId] ?? null) : mine ? me.comment : null,
      },
    ];
  });

  // The host reads the clue off their own screen and sees it on selection; the room
  // waits for the reveal, and for a daily double it waits out the wager too.
  const prompt = host ? host.prompt : (shared.current?.prompt ?? null);
  const clueAnswer = host ? host.answer : (shared.current?.answer ?? null);

  // Hidden from the room until the clue resolves; the host and the wagering player see
  // it immediately.
  const mineToWager = shared.wager !== null && shared.wager.participantId === you.participantId;
  const wagerAmount = host
    ? host.wagerAmount
    : mineToWager && me
      ? me.wagerAmount
      : (shared.wager?.amount ?? null);

  return {
    seq: snapshot.seq,
    core,
    you,
    shared,
    host,
    me,
    standings,
    buzzes,
    answers,
    prompt,
    clueAnswer,
    wagerAmount,
    requests: snapshot.requests,
    nameOf,
    scoreOf,
    ask: (kind) => snapshot.requests.find((r) => r.spec.kind === kind) ?? null,
  };
}

/**
 * Whether leaving now spends the clue. Mirrors `reduce.ts`'s `returnToBoard` by hand
 * (different shapes); only drives the TV's Back-button tooltip, the server decides.
 */
export function clueIsSpent(view: JeopardyView): boolean {
  return (
    view.shared.phase === 'resolved' ||
    view.shared.buzzes.some((b) => b.verdict !== null) ||
    view.shared.answers.some((a) => a.verdict !== null)
  );
}
